import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  TextareaAutosize,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useStyles from "./styles";
import { useDispatch, useSelector } from "react-redux";
import { STATUS } from "../../../redux/constants";
import CeroInput from "../../../components/CeroInput";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate, useParams } from "react-router-dom";
import CeroButton from "../../../components/CeroButton";
import { useSnackbar } from "notistack";
import axios from "axios";
import { styled } from "@mui/material/styles";
import { APIEndpoints } from "../../../redux/constants";
import {
  getCountryList,
  getSustainabilityRiskSurveyQuestions,
  resetSustainabilityRiskScreeningStatus,
  sustainabilityRiskSurveyResponse,
} from "../../../redux/actions";

const SustainabilityRiskAssessmentResponse = () => {
  const classes = useStyles();
  const [selectedValues, setSelectedValues] = useState({});
  const dispatch = useDispatch();
  const { answerId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [textAnswers, setTextAnswers] = useState({});
  const [companyName, setCompanyName] = useState("");

  const [supplierInfoAnswers, setSupplierInfoAnswers] = useState({});

  const supplierQuestionsList = useSelector((state) => {
    const data =
      state.sustainabilityRiskAssessment
        .getSustainabilityRiskAssessementSurveyQuestions.data;
    return Array.isArray(data) ? data : [];
  });
  const questionListStatus = useSelector(
    (state) =>
      state.sustainabilityRiskAssessment
        .getSustainabilityRiskAssessementSurveyQuestions.status
  );
  const formStatus = useSelector(
    (state) =>
      state.sustainabilityRiskAssessment
        .sustainabilityRiskAssessementSurveyResponse
  );

  const handleCheckboxChange = (event, questionId) => {
    const value = event.target.value;
    setSelectedValues((prevValues) => {
      const questionValues = prevValues[questionId] || [];
      if (questionValues.includes(value)) {
        return {
          ...prevValues,
          [questionId]: questionValues.filter((item) => item !== value),
        };
      } else {
        return {
          ...prevValues,
          [questionId]: [...questionValues, value],
        };
      }
    });
  };

  const handleFileUpload = async (file, questionId) => {
    const formData = new FormData();
    for (let i = 0; i < file.length; i++) formData.append("file", file[i]);
    try {
      const response = await axios.post(
        APIEndpoints.SURVEY_FILE_UPLOAD(answerId, questionId),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const blobUrl = response.data?.blob_url;
      enqueueSnackbar("File uploaded successfully.", {
        variant: "success",
      });
      if (blobUrl) {
        setTextAnswers((prevAnswers) => ({
          ...prevAnswers,
          [`file-${questionId}`]: blobUrl,
        }));
      }
    } catch (error) {
      enqueueSnackbar("File upload failed. Please try again.", {
        variant: "error",
      });
    }
  };

  const onSubmit = () => {
    const answerList = supplierQuestionsList.map((question) => {
      let answer = "";
      let answer_value = "";
      if (
        question.question_type === "text" ||
        question.question_type === "Text Field"
      ) {
        answer = textAnswers[question.id] || "";
      } else if (
        question.question_type === "choose_from_option" ||
        question.question_type === "Choose from Options"
      ) {
        answer = selectedValues[question.id] || "";

        if (answer === "Other (please specify)") {
          answer_value = textAnswers[`other-${question.id}`] || "";
        }
      } else if (question.question_type === "multiselect_check_box") {
        const selectedOptions = selectedValues[question.id] || [];
        answer = selectedOptions.join(", ");
        if (selectedOptions.includes("Other (please specify)")) {
          answer_value = textAnswers[`other-${question.id}`] || "";
        }
      } else if (question.question_type === "upload") {
        answer = textAnswers[`file-${question.id}`] || "";
      }
      return {
        question_id: question.id,
        answer,
        question_type: question.question_type,
        ...(answer_value && { answer_value }),
      };
    });
    Object.entries(supplierInfoAnswers).forEach(([key, value]) => {
      answerList.push({
        question_id: key,
        answer: value,
        question_type: typeof value === "object" ? "object" : "text",
        answer_value: "",
      });
    });
    const payload = {
      answer_list: answerList,
      company_name: companyName,
    };

    dispatch(sustainabilityRiskSurveyResponse(answerId, payload));
  };

  useEffect(() => {
    if (formStatus.status === STATUS.SUCCESS) {
      navigate("/supplier-assessment/survey-response");
    } else if (formStatus.status === STATUS.ERROR) {
      enqueueSnackbar(
        formStatus.message ||
          "We couldn't process your request. Please try again later.",
        {
          variant: "error",
          autoHideDuration: 3000,
        }
      );
      dispatch(resetSustainabilityRiskScreeningStatus());
    }
  }, [formStatus, dispatch, enqueueSnackbar]);

  const handleTextInputChange = (questionId, value) => {
    setTextAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: value,
    }));
  };

  const handleSingleSelect = (questionId, selectedOption) => {
    setSelectedValues((prevSelectedValues) => ({
      ...prevSelectedValues,
      [questionId]: selectedOption,
    }));

    if (selectedOption === "Other") {
      setTextAnswers((prevAnswers) => ({
        ...prevAnswers,
        [`other-${questionId}`]: "",
      }));
    }
  };

  useEffect(() => {
    if (answerId) {
      dispatch(getSustainabilityRiskSurveyQuestions(answerId));
      dispatch(getCountryList());
    }
  }, [dispatch, answerId]);

  const groupedQuestions = supplierQuestionsList?.reduce((acc, question) => {
    const section = question.section || "Uncategorized";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(question);
    return acc;
  }, {});

  function reorderLists(data) {
    Object.keys(data).forEach((key) => {
      const list = data[key];
      const reorderedList = [];
      const itemMap = new Map();

      // Create a map of items based on their `_id`
      list.forEach((item) => {
        itemMap.set(item.id, item);
      });

      // Iterate through the list and reorder dynamically
      list.forEach((item) => {
        if (!reorderedList.includes(itemMap.get(item.id))) {
          reorderedList.push(item); // Add the main item first
        }
        // Find and add all items with matching `question_id`
        const relatedItems = list.filter(
          (related) => related.question_id === item.id
        );
        relatedItems.forEach((relatedItem) => {
          if (!reorderedList.includes(relatedItem)) {
            reorderedList.push(relatedItem);
          }
        });
      });

      // Add any remaining items that weren't matched
      const remainingItems = list.filter(
        (item) => !reorderedList.includes(item)
      );
      reorderedList.push(...remainingItems);

      // Update the original list with the reordered one
      data[key] = reorderedList;
    });

    return data;
  }

  const showSubQuestion = (subQuestion) => {
    let mainQtn = supplierQuestionsList.find(
      (item) => item.id === subQuestion.question_id
    );
    if (
      mainQtn.question_type === "choose_from_option" ||
      mainQtn.question_type === "Choose from Options"
    ) {
      if (
        selectedValues[subQuestion.question_id]
          ?.toLowerCase()
          .includes(subQuestion.show_based_on)
      )
        return true;
    } else if (mainQtn.question_type === "multiselect_check_box") {
      if (
        selectedValues[subQuestion.question_id]?.some((item) =>
          item.toLowerCase().includes(subQuestion.show_based_on)
        )
      )
        return true;
    } else if (mainQtn.question_type === "text") {
      if (
        textAnswers[subQuestion.question_id]
          ?.toLowerCase()
          .includes(subQuestion.show_based_on)
      )
        return true;
    }
    return false;
  };
  const requiredFields = [];
  const requiredAnswers = {};
  const isFormValid = () => {
    return requiredFields.every((field) => {
      const value = selectedValues[field] || textAnswers[field];
      return value;
    });
  };

  const VisuallyHiddenInput = styled("input")({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    border: 0,
  });

  let questionNumber = 1;

  return (
    <Paper className={classes.container}>
      <Box mb={4} display="flex" justifyContent="space-between" paddingY={2}>
        <Typography fontSize={20} fontWeight={550} variant="h3">
          SUSTAINABILITY RISK ASSESSMENT QUESTIONNAIRE
        </Typography>
      </Box>
      {questionListStatus === STATUS.SUCCESS ? (
        <>
          {Object.entries(reorderLists(groupedQuestions))?.map(
            ([sectionTitle, questions], index) => {
              const romanNumbers = [
                "I",
                "II",
                "III",
                "IV",
                "V",
                "VI",
                "VII",
                "VIII",
                "IX",
                "X",
              ];

              return (
                <Box key={sectionTitle} mb={4}>
                  <Typography
                    fontSize={23}
                    fontWeight={600}
                    className={classes.sectionTitle}
                    mb={2}
                    sx={{ textDecoration: "none" }}
                  >
                    {`${romanNumbers[index] || index + 1}. ${sectionTitle}`}
                  </Typography>

                  {questions?.map((question) => {
                    if (question.is_mandatory) {
                      requiredFields.push(question.id);
                    }
                    if (question.question_id === null) {
                      const currentQuestionNumber = questionNumber;
                      questionNumber += 1;

                      return (
                        <Grid
                          key={question.id}
                          className={
                            question.question_type === "text"
                              ? classes.inputGrid
                              : classes.gridItem
                          }
                        >
                          <div style={{ display: "flex", marginBottom: "5px" }}>
                            <Typography
                              fontSize={18}
                              fontWeight={500}
                              variant="body1"
                              className={classes.mainQuestion}
                            >
                              {`${currentQuestionNumber}. ${question.question}`}
                            </Typography>
                            {question.is_mandatory && (
                              <Typography
                                className={classes.starMark}
                              >{`*`}</Typography>
                            )}
                          </div>

                          <Box
                            display="flex"
                            flexDirection="column"
                            paddingLeft={6}
                            className={classes.layout}
                          >
                            {question.question_type ===
                              "multiselect_check_box" &&
                              question.options.map((option, idx) => {
                                return (
                                  <>
                                    <FormControlLabel
                                      key={idx}
                                      control={
                                        <Checkbox
                                          className={classes.selectCheckbox}
                                          checked={
                                            selectedValues[
                                              question.id
                                            ]?.includes(option) || false
                                          }
                                          onChange={(e) =>
                                            handleCheckboxChange(e, question.id)
                                          }
                                          value={option}
                                          sx={{
                                            color: "#5A404E !important",
                                            "&.Mui-checked": {
                                              color: "#8C2148 !important",
                                            },
                                          }}
                                        />
                                      }
                                      label={option}
                                      className={classes.label}
                                    />
                                    {selectedValues[question.id]?.includes(
                                      "Other (please specify)"
                                    ) &&
                                      option === "Other (please specify)" && (
                                        <CeroInput
                                          classes={{
                                            container: classes.inputContainer,
                                          }}
                                          variant="outlined"
                                          placeholder="Please type your Answer"
                                          multiline
                                          rows={3}
                                          value={
                                            textAnswers[
                                              `other-${question.id}`
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            handleTextInputChange(
                                              `other-${question.id}`,
                                              e.target.value
                                            )
                                          }
                                          className={classes.input}
                                        />
                                      )}
                                  </>
                                );
                              })}

                            {(question.question_type === "choose_from_option" ||
                              question.question_type ===
                                "Choose from Options") &&
                              question.options.map((option, idx) => {
                                return (
                                  <>
                                    <FormControlLabel
                                      key={idx}
                                      control={
                                        <Checkbox
                                          className={classes.selectCheckbox}
                                          checked={
                                            selectedValues[question.id] ===
                                            option
                                          }
                                          onChange={() => {
                                            handleSingleSelect(
                                              question.id,
                                              option
                                            );
                                          }}
                                          value={option}
                                          sx={{
                                            color: "#5A404E !important",
                                            borderWidth: "none",
                                            "&.Mui-checked": {
                                              color: "#8C2148 !important",
                                            },
                                          }}
                                        />
                                      }
                                      label={option}
                                    />
                                    {selectedValues[question.id] ===
                                      "Other (please specify)" &&
                                      option === "Other (please specify)" && (
                                        <TextField
                                          variant="outlined"
                                          placeholder="Please type your Answer"
                                          multiline
                                          rows={3}
                                          value={
                                            textAnswers[
                                              `other-${question.id}`
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            handleTextInputChange(
                                              `other-${question.id}`,
                                              e.target.value
                                            )
                                          }
                                          className={classes.input}
                                        />
                                      )}
                                  </>
                                );
                              })}
                            {question.question_type === "upload" && (
                              <Box className={classes.upload}>
                                <Button
                                  component="label"
                                  role={undefined}
                                  variant="contained"
                                  tabIndex={-1}
                                  className={classes.buttonPrimary}
                                  startIcon={<CloudUploadIcon />}
                                >
                                  Upload files
                                  <VisuallyHiddenInput
                                    type="file"
                                    onChange={(e) => {
                                      if (e.target.files.length) {
                                        handleFileUpload(
                                          e.target.files,
                                          question.id
                                        );
                                      }
                                    }}
                                    multiple
                                  />
                                </Button>
                              </Box>
                            )}
                            {question.question_type === "text" && (
                              <Box
                                sx={{
                                  paddingLeft: "2px",
                                  marginTop: "8px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    marginBottom: "4px",
                                    display: "block",
                                    color: "#666",
                                  }}
                                ></Typography>
                                <TextField
                                  label="Please type your Answer"
                                  multiline
                                  minRows={3}
                                  variant="outlined"
                                  value={textAnswers[question.id] || ""}
                                  onChange={(e) =>
                                    handleTextInputChange(
                                      question.id,
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  className={classes.customTextField}
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    }
                    const subQuestionLetter = String.fromCharCode(
                      96 +
                        supplierQuestionsList
                          .filter((q) => q.question_id === question.question_id)
                          .indexOf(question) +
                        1
                    );
                    return (
                      <Grid key={question.id} paddingLeft={6} mb={2}>
                        {(!question?.show_based_on ||
                          showSubQuestion(question)) && (
                          <>
                            <div
                              style={{ display: "flex", marginBottom: "5px" }}
                            >
                              <Typography className={classes.subQuestion}>
                                {`${subQuestionLetter}. ${question.question}`}
                              </Typography>
                              {question.is_mandatory && (
                                <Typography
                                  className={classes.starMark}
                                >{`*`}</Typography>
                              )}
                            </div>
                            {(question.question_type === "choose_from_option" ||
                              question.question_type ===
                                "Choose from Options") &&
                              question.options.map((option, idx) => {
                                return (
                                  <>
                                    <FormControlLabel
                                      key={idx}
                                      control={
                                        <Checkbox
                                          className={classes.selectCheckbox}
                                          checked={
                                            selectedValues[question.id] ===
                                            option
                                          }
                                          onChange={() =>
                                            handleSingleSelect(
                                              question.id,
                                              option
                                            )
                                          }
                                          value={option}
                                          sx={{
                                            color: "#5A404E !important",
                                            borderWidth: "none",
                                            "&.Mui-checked": {
                                              color: "#8C2148 !important",
                                            },
                                          }}
                                        />
                                      }
                                      label={option}
                                    />
                                    {selectedValues[question.id] ===
                                      "Other (please specify)" &&
                                      option === "Other (please specify)" && (
                                        <TextField
                                          variant="outlined"
                                          placeholder="Please type your Answer"
                                          multiline
                                          rows={3}
                                          value={
                                            textAnswers[
                                              `other-${question.id}`
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            handleTextInputChange(
                                              `other-${question.id}`,
                                              e.target.value
                                            )
                                          }
                                          className={classes.input}
                                        />
                                      )}
                                  </>
                                );
                              })}
                            {question.question_type ===
                              "multiselect_check_box" &&
                              question.options.map((option, idx) => {
                                return (
                                  <>
                                    <FormControlLabel
                                      key={idx}
                                      control={
                                        <Checkbox
                                          className={classes.selectCheckbox}
                                          checked={
                                            selectedValues[
                                              question.id
                                            ]?.includes(option) || false
                                          }
                                          onChange={(e) =>
                                            handleCheckboxChange(e, question.id)
                                          }
                                          value={option}
                                          sx={{
                                            color: "#5A404E !important",
                                            "&.Mui-checked": {
                                              color: "#8C2148 !important",
                                            },
                                          }}
                                        />
                                      }
                                      label={option}
                                      className={classes.label}
                                    />
                                    {selectedValues[question.id]?.includes(
                                      "Other (please specify)"
                                    ) &&
                                      option === "Other (please specify)" && (
                                        <CeroInput
                                          classes={{
                                            container: classes.inputContainer,
                                          }}
                                          variant="outlined"
                                          placeholder="Please type your Answer"
                                          multiline
                                          rows={3}
                                          value={
                                            textAnswers[
                                              `other-${question.id}`
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            handleTextInputChange(
                                              `other-${question.id}`,
                                              e.target.value
                                            )
                                          }
                                          className={classes.input}
                                        />
                                      )}
                                  </>
                                );
                              })}
                            {question.question_type === "text" && (
                              <Box
                                sx={{
                                  paddingLeft: "2px",
                                  marginTop: "8px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    marginBottom: "4px",
                                    display: "block",
                                    color: "#666",
                                  }}
                                ></Typography>
                                <TextField
                                  label="Please type your Answer"
                                  multiline
                                  minRows={3}
                                  variant="outlined"
                                  value={textAnswers[question.id] || ""}
                                  onChange={(e) =>
                                    handleTextInputChange(
                                      question.id,
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  className={classes.customTextField}
                                />
                              </Box>
                            )}

                            {question.question_type === "upload" && (
                              <Box className={classes.upload}>
                                <Button
                                  component="label"
                                  role={undefined}
                                  variant="contained"
                                  tabIndex={-1}
                                  className={classes.buttonPrimary}
                                  startIcon={<CloudUploadIcon />}
                                >
                                  Upload files
                                  <VisuallyHiddenInput
                                    type="file"
                                    onChange={(e) => {
                                      if (e.target.files.length) {
                                        handleFileUpload(
                                          e.target.files,
                                          question.id
                                        );
                                      }
                                    }}
                                    multiple
                                  />
                                </Button>
                              </Box>
                            )}
                          </>
                        )}
                      </Grid>
                    );
                  })}
                </Box>
              ); // <-- close the return (...) of the section
            }
          )}

          <div className={classes.buttonSubmitContainer}>
            <CeroButton
              buttonText="Submit Survey"
              className={classes.buttonPrimary}
              onClick={onSubmit}
              disabled={!isFormValid()}

              // disabled={!isFormValid()} // Disable if invalid
            />
          </div>
        </>
      ) : (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
    </Paper>
  );
};

export default SustainabilityRiskAssessmentResponse;
